import { Injectable } from '@angular/core';

import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { of, throwError, zip } from 'rxjs';

import { concatMap, map, tap } from 'rxjs/operators';
import { AuthConfig, OAuthModuleConfig } from 'angular-oauth2-oidc';
import { TranslateService } from '@ngx-translate/core';

type ConfigKey =
  | 'baseURLConfig'
  | 'appConfig'
  | 'APIMConfig'
  | 'authConfig'
  | 'commonAPIConfig'
  | 'featuresConfig'
  | 'defaultPreferenceTemplate'
  | 'settingsConfig';

@Injectable()
export class RunTimeConfigService {
  private configPathPropertiesFile = 'assets/configs/config-paths.properties';
  private configData: any = {};
  private appSpecificFeaturesData: any = {};
  private appSpecificConfigData: any = {};

  private http: HttpClient;
  private configsPaths;
  private oAuthModuleConfig: OAuthModuleConfig = {
    resourceServer: {
      allowedUrls: [],
      sendAccessToken: true
    }
  };
  // private randomKey;

  constructor(handler: HttpBackend, private translate: TranslateService) {
    // this.randomKey = sessionStorage.getItem('r');
    //
    // if (!this.randomKey) {
    //   this.randomKey = Date.now();
    //   sessionStorage.setItem('r', this.randomKey);
    // }

    this.http = new HttpClient(handler);
  }

  // export function translateInitializerFactory(translate: TranslateService): () => Promise<any> {
  //   return () => {
  //     translate.setDefaultLang('en-US');
  //     return translate.use('en-US').toPromise();
  //   };
  // }

  load() {
    return this.getPaths()
      .pipe(
        concatMap(configPaths => {
          return zip(
            this.loadConfig('baseURLConfig'),
            this.loadConfig('appConfig'),
            this.loadConfig('APIMConfig'),
            this.loadConfig('authConfig'),
            this.loadConfig('commonAPIConfig'),
            this.loadConfig('featuresConfig'),
            this.loadConfig('settingsConfig'),
            this.loadConfig('defaultPreferenceTemplate')
          );
        })
      )
      .toPromise();
  }

  getConfig(configKey: ConfigKey) {
    return this.configData[configKey];
  }

  getAppSpecificFeatures(configFile: string) {
    if (!this.configsPaths['pathToAppSpecificFeatures']) {
      console.warn('*** Warning: Failed to load app specific features! All features are enabled by default.. ***');
      return of({});
    }

    // if config was already reloaded - return from memory cache
    if (this.appSpecificFeaturesData[configFile]) {
      return of(this.appSpecificFeaturesData[configFile]);
    }
    // else load config file and put in memory cache
    else {
      return this.http
        .get(this.configsPaths['pathToAppSpecificFeatures'] + configFile, {
          // params: {
          //   r: this.randomKey
          // }
        })
        .pipe(
          tap((res: any) => {
            this.appSpecificFeaturesData[configFile] = JSON.parse(JSON.stringify(res));
          })
        );
    }
  }

  getAppSpecificSettings(configFile: string) {
    if (!this.configsPaths['pathToAppSpecificSettings']) {
      console.warn('*** Warning: Failed to load app specific config settings! ***');
      return of({});
    }

    // if config was already reloaded - return from memory cache
    if (this.appSpecificConfigData[configFile]) {
      return of(this.appSpecificConfigData[configFile]);
    }
    // else load config file and put in memory cache
    else {
      return this.http
        .get(this.configsPaths['pathToAppSpecificSettings'] + configFile, {
          // params: {
          //   r: this.randomKey
          // }
        })
        .pipe(
          tap((res: any) => {
            this.appSpecificConfigData[configFile] = JSON.parse(JSON.stringify(res));
          })
        );
    }
  }

  // oauth oidc
  getOAuthConfig() {
    // const authConfig = this.getConfig('authConfig');
    // const baseURLConfig = this.getConfig('baseURLConfig');
    // return {
    //   issuer: authConfig.oAuth2OIDC.issuer,
    //   clientId: authConfig.oAuth2OIDC.clientId,
    //   responseType: authConfig.oAuth2OIDC.responseType,
    //   redirectUri: baseURLConfig.app.baseURL + authConfig.oAuth2OIDC.redirectUri || baseURLConfig.app.baseURL,
    //   postLogoutRedirectUri: baseURLConfig.app.baseURL + authConfig.oAuth2OIDC.postLogoutRedirectUri,
    //   silentRefreshRedirectUri: baseURLConfig.app.baseURL + authConfig.oAuth2OIDC.silentRefreshRedirectUri,
    //   scope: authConfig.oAuth2OIDC.scope,
    //   silentRefreshShowIFrame: authConfig.oAuth2OIDC.useSilentRefresh,
    //   silentRefreshTimeout: authConfig.oAuth2OIDC.silentRefreshTimeout,
    //   timeoutFactor: authConfig.oAuth2OIDC.timeoutFactor,
    //   sessionChecksEnabled: authConfig.oAuth2OIDC.sessionChecksEnabled,
    //   showDebugInformation: authConfig.oAuth2OIDC.showDebugInformation,
    //   clearHashAfterLogin: authConfig.oAuth2OIDC.clearHashAfterLogin,
    //   nonceStateSeparator: authConfig.oAuth2OIDC.nonceStateSeparator,
    //   strictDiscoveryDocumentValidation: authConfig.oAuth2OIDC.strictDiscoveryDocumentValidation,
    //   requireHttps: false,
    //   skipIssuerCheck: true,
    //   disablePKCE: false,
    //   customQueryParams: authConfig.oAuth2OIDC.customQueryParams ? authConfig.oAuth2OIDC.customQueryParams : null
    // };
  }

  // oauth oidc
  getOAuthModuleConfig(): OAuthModuleConfig {
    return this.oAuthModuleConfig;

    // const authConfig = this.getConfig('authConfig');
    // const baseURLConfig = this.getConfig('baseURLConfig');
    //
    // return {
    //   resourceServer: {
    //     allowedUrls: [],
    //     sendAccessToken: true
    //   }
    // };
  }

  private loadConfig(configKey: ConfigKey) {
    return this.http
      .get(this.configsPaths[configKey], {
        // params: {
        //   r: this.randomKey
        // }
      })
      .pipe(
        tap((res: any) => {
          this.configData[configKey] = res;

          // if (configKey === 'baseURLConfig') {
          //   this.oAuthModuleConfig.resourceServer.allowedUrls.push(res.APIM.baseURL);
          // }
        })
      );
  }

  private getPaths() {
    const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
    return this.http.get(this.configPathPropertiesFile, { headers, responseType: 'text' }).pipe(
      map((res: any) => {
        const props = this.extractProperties(res.replace(/"/g, ''));
        if (props && Object.keys(props).length > 0) {
          this.configsPaths = props;
        } else {
          throwError('Invalid properties file');
        }
      })
    );
  }

  private extractProperties(data: any) {
    const lines = data.split('\n');
    const properties = {};

    for (const l of lines) {
      const line = l.trim();
      if (!line || line[0] === '#') {
        continue;
      }
      const keyValue = line.split('=');
      const key = keyValue[0].trim();
      const value = keyValue[1].trim();
      properties[key] = value;
    }

    return properties;
  }
}
